import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { BenchmarkType, IndicatorName, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { WaterHistoricalChart } from '@/components/Charts';
import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import { useChartZoom } from '@/components/Charts/components/ChartZoom';
import { ItemizedGraphTypeRenderer } from '@/components/Charts/components/ItemizedGraphType';
import { NUM_ITEMS_TYPE } from '@/components/Charts/hooks/usePlotsForItemizedGraph';
import { GraphFact } from '@/components/Charts/types';
import { getChartDataWithBenchmarks } from '@/components/Charts/utils';
import { GraphTileProps } from '@/components/IndicatorCard/GraphTile';
import { useNCCardContext } from '@/components/NCCard/NCCard';
import { useGetCustomBenchmarks } from '@/hooks/useCustomBenchmarks';
import { useScaledCustomBenchmark } from '@/hooks/useScaledCustomBenchmark';
import { useBenchmarksForOrganization } from '@/pages/shared/hooks/useBenchmarkForFact';
import {
  useOrganizationIndicatorGraphFact,
  useOrganizationIndicatorReportingFact,
} from '@/pages/shared/hooks/useOrganizationFact';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';

import { NoData } from '../../../../components/NoData';

export const WaterCardGraphTile = (props?: (GraphTileProps & { selectedBenchmark?: string | null }) | null) => {
  const { t } = useTranslation();

  const organizationId = useOrganizationId();

  const indicator = GraphTileType.historic_water_soil_moisture;
  const { data: customBenchmarksResp } = useGetCustomBenchmarks(organizationId, indicator);

  const selectedBenchmark = props?.selectedBenchmark;
  const activeCustomBenchmark = customBenchmarksResp?.results?.find((b) => b.id === selectedBenchmark);

  const { analysisType } = useNCCardContext();
  const isTotal = analysisType === 'total';
  const displayCustomBenchmark = useScaledCustomBenchmark(activeCustomBenchmark, isTotal);

  const { zoom, setZoom } = useChartZoom();
  const selectedZoom = props?.zoom ?? zoom; // props.zoom is used when this component is rendered as widget (e.g. measurement pages)
  const setZoomFunc = props?.setZoom ?? setZoom;
  const plotGroupId = props?.plotGroupId ?? null;

  const historicWaterAndSoilMoistureFact =
    analysisType === 'total'
      ? IndicatorName.water_and_soil_moisture_historic_total_graph
      : IndicatorName.water_and_soil_moisture_historic_per_ha_graph;
  const { [historicWaterAndSoilMoistureFact]: historicWaterAndSoilMoisture } = useOrganizationIndicatorGraphFact<
    GraphFact[]
  >(historicWaterAndSoilMoistureFact, null, undefined, plotGroupId);
  const whcData = historicWaterAndSoilMoisture?.value;

  const benchmarkFactType =
    analysisType === 'total' ? IndicatorName.water_fc_total_benchmark : IndicatorName.water_fc_per_ha_benchmark;

  const { [benchmarkFactType]: whcLowBenchmark } = useOrganizationIndicatorReportingFact<number>(
    benchmarkFactType,
    null,
    { [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low },
    plotGroupId,
  );

  const { [benchmarkFactType]: whcModerateBenchmark } = useOrganizationIndicatorReportingFact<number>(
    benchmarkFactType,
    null,
    { [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate },
    plotGroupId,
  );

  const { [benchmarkFactType]: whcHighBenchmark } = useOrganizationIndicatorReportingFact<number>(
    benchmarkFactType,
    null,
    { [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high },
    plotGroupId,
  );

  const whcBenchmarks = useBenchmarksForOrganization(benchmarkFactType, null, undefined, plotGroupId);

  const noChartData = !whcData?.length;

  if (noChartData) {
    return <NoData />;
  }

  const chartData = activeCustomBenchmark
    ? [...whcData]
    : [
        ...whcData,
        ...getChartDataWithBenchmarks({
          chartData: whcData,
          benchmarks: { low: whcLowBenchmark, moderate: whcModerateBenchmark, high: whcHighBenchmark },
        }),
      ];

  return (
    <Stack center data-testid='water-historic-graph-tile'>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-6 lg:pl-1'>
        {!props?.headerProps?.hideChartHeader && (
          <ChartHeading
            heading={t('shared.ncaDetail.details.water.labels.historicalWHCGraphHeadline')}
            popover={{
              title: t('shared.ncaDetail.details.water.explainers.historicalData.title'),
              body: t('shared.ncaDetail.details.water.explainers.historicalData.body'),
            }}
            className='w-full px-6 sm:px-0'
            zoom={selectedZoom}
            setZoom={setZoom}
          />
        )}
        <ItemizedGraphTypeRenderer
          graphType={props?.graphType}
          indicator={GraphTileType.historic_water_soil_moisture}
          plotRenderType={props?.plotRenderType ?? NUM_ITEMS_TYPE.TOP_PLOTS}
          plotGroupId={plotGroupId ?? undefined}
          zoom={selectedZoom}
          setZoom={setZoomFunc}
          customBenchmark={displayCustomBenchmark}
          defaultGraphType={
            <WaterHistoricalChart
              analysisType={analysisType}
              data={chartData}
              benchmarks={whcBenchmarks}
              customBenchmark={displayCustomBenchmark}
              showTooltip={true}
              className='relative'
              data-testid='water-historical-chart'
              zoom={selectedZoom}
              setZoom={setZoomFunc}
              plotGroupId={plotGroupId ?? undefined}
            />
          }
        />
      </Stack>
    </Stack>
  );
};
